import type { PrintCmyk } from "./print-color.interface";

const HEX6 = /^#?([0-9A-Fa-f]{6})$/;
const HEX3 = /^#?([0-9A-Fa-f]{3})$/;

export function normalizePrintHex(
  value: string | null | undefined,
): string | null {
  const raw = (value ?? "").trim();
  if (!raw) {
    return null;
  }
  const hex6 = raw.match(HEX6);
  if (hex6) {
    return `#${hex6[1].toUpperCase()}`;
  }
  const hex3 = raw.match(HEX3);
  if (hex3) {
    const [r, g, b] = hex3[1].split("");
    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
  }
  return null;
}

export function isPrintHexColor(value: string | null | undefined): boolean {
  return normalizePrintHex(value) != null;
}

function clampChannel(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) {
    return min;
  }
  return Math.min(max, Math.max(min, value));
}

function roundCmyk(value: number): number {
  return Math.round(clampChannel(value, 0, 100) * 10) / 10;
}

export function hexToCmyk(hex: string | null | undefined): PrintCmyk {
  const normalized = normalizePrintHex(hex) ?? "#000000";
  const r = Number.parseInt(normalized.slice(1, 3), 16) / 255;
  const g = Number.parseInt(normalized.slice(3, 5), 16) / 255;
  const b = Number.parseInt(normalized.slice(5, 7), 16) / 255;
  const k = 1 - Math.max(r, g, b);
  if (k >= 1 - 1e-9) {
    return { c: 0, m: 0, y: 0, k: 100 };
  }
  const denom = 1 - k;
  return {
    c: roundCmyk(((1 - r - k) / denom) * 100),
    m: roundCmyk(((1 - g - k) / denom) * 100),
    y: roundCmyk(((1 - b - k) / denom) * 100),
    k: roundCmyk(k * 100),
  };
}

export function cmykToHex(cmyk: PrintCmyk): string {
  const c = clampChannel(cmyk.c, 0, 100) / 100;
  const m = clampChannel(cmyk.m, 0, 100) / 100;
  const y = clampChannel(cmyk.y, 0, 100) / 100;
  const k = clampChannel(cmyk.k, 0, 100) / 100;
  const r = Math.round(255 * (1 - c) * (1 - k));
  const g = Math.round(255 * (1 - m) * (1 - k));
  const b = Math.round(255 * (1 - y) * (1 - k));
  const toHex = (channel: number) =>
    clampChannel(channel, 0, 255).toString(16).padStart(2, "0").toUpperCase();
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function hexToCmykCss(hex: string | null | undefined): string {
  const { c, m, y, k } = hexToCmyk(hex);
  const toUnit = (value: number) =>
    (Math.round((clampChannel(value, 0, 100) / 100) * 1000) / 1000).toString();
  return `cmyk(${toUnit(c)}, ${toUnit(m)}, ${toUnit(y)}, ${toUnit(k)})`;
}

export function clampCmykChannel(value: number): number {
  return roundCmyk(value);
}
